/**
 * Cliente del endpoint /api/v1/tenants/{id}/stats.
 * Backend: app/api/v1/tenants.py.
 *
 * Por ahora solo exponemos el stats endpoint — la edición del tenant
 * (settings, plan tier, etc.) llega en Fase F cuando exista la pantalla
 * de Configuración.
 */

import { apiFetch } from './client'
import type { UserTenantRole } from './me'

export interface TenantStatsResponse {
  active_projects: number
  /** API contract field — surfaced in UI as "official_published" / "Documentos oficiales". */
  canon_published: number
  /** Placeholder hasta Fase E (change_requests). Siempre 0 en MVP. */
  open_crs: number
  /** Cuenta users con status='active' del tenant. */
  members: number
}

export async function fetchTenantStats(
  accessToken: string,
  tenantId: string,
): Promise<TenantStatsResponse> {
  return apiFetch<TenantStatsResponse>(
    `/api/v1/tenants/${tenantId}/stats`,
    { accessToken },
  )
}

// ─── /tenants/{id}/users (B.5.e.4 — picker de members internos) ────────────

export type TenantUserStatus = 'active' | 'pending' | 'disabled'
export type TenantUserLocale = 'es' | 'en'

export interface TenantUserSummary {
  id: string
  email: string
  display_name: string
  /** Tier de licencia del user en el tenant. */
  role: UserTenantRole
  status: TenantUserStatus
  locale: TenantUserLocale
  created_at: string
  /** OT-0008 §4.2.1 (D19) — lectura transversal: ve todos los proyectos
   *  del tenant, en solo lectura, sin estar en su Equipo & RACI.
   *  Prohibido para tier `external`. */
  can_view_all_projects: boolean
}

export interface ListTenantUsersFilters {
  user_status?: TenantUserStatus | undefined
}

/** Lista usuarios del tenant para pickers (e.g. añadir miembro interno
 * a un proyecto). El backend solo permite consultar el tenant activo
 * del caller — pasar un tenantId distinto devuelve 404. */
export async function listTenantUsers(
  accessToken: string,
  tenantId: string,
  filters?: ListTenantUsersFilters,
): Promise<TenantUserSummary[]> {
  const params = new URLSearchParams()
  if (filters?.user_status) params.set('user_status', filters.user_status)
  const qs = params.toString()
  return apiFetch<TenantUserSummary[]>(
    `/api/v1/tenants/${tenantId}/users${qs ? `?${qs}` : ''}`,
    { accessToken },
  )
}

/** Actualiza flags de un usuario del tenant. Solo admin.
 *
 * El **tier** no se cambia por aquí a propósito: mover a alguien de
 * `editor` a `reviewer` cambia lo que se le factura y lo que puede hacer
 * con documentos que ya posee, así que merece su propio flujo con
 * confirmación en vez de un toggle suelto. */
export async function updateTenantUser(
  accessToken: string,
  tenantId: string,
  userId: string,
  body: { can_view_all_projects: boolean },
): Promise<TenantUserSummary> {
  return apiFetch<TenantUserSummary>(
    `/api/v1/tenants/${tenantId}/users/${userId}`,
    { accessToken, method: 'PATCH', body },
  )
}

/** Cambia el tier de licencia de un usuario del tenant (OT-0020). Único
 * flujo soportado: el backend hace cumplir 6 invariantes (nunca dejar el
 * tenant sin admin, no degradar a quien posee documentos o lidera un
 * proyecto sin decirlo primero, etc.) y deja rastro en auditoría. Ver
 * `Docs/ordenes-de-trabajo/feat/OT-0020-cambio-de-tier.md`.
 *
 * `confirm_admin` es obligatorio cuando `tier === 'admin'` — espejo de
 * `createInvite`. */
export async function changeTenantUserTier(
  accessToken: string,
  tenantId: string,
  userId: string,
  body: { tier: UserTenantRole; confirm_admin?: boolean },
): Promise<TenantUserSummary> {
  return apiFetch<TenantUserSummary>(
    `/api/v1/tenants/${tenantId}/users/${userId}/tier`,
    { accessToken, method: 'POST', body },
  )
}
